import React, { useState } from 'react';
import { BannerElement, BannerSize, DataFeed } from '../types';
import { X, Download, ZoomIn, ZoomOut, Grid, List } from 'lucide-react';

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  elements: BannerElement[];
  size: BannerSize;
  dataFeedArray: DataFeed[];
}

export const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  elements,
  size,
  dataFeedArray
}) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [previewScale, setPreviewScale] = useState(0.3);

  if (!isOpen) return null;

  const processContent = (content: string, dataBinding?: string, dataFeed?: DataFeed) => {
    if (dataBinding && dataFeed) {
      return String(dataFeed[dataBinding] || content);
    }
    
    if (!dataFeed) return content;
    
    return content.replace(/\{\{(\w+)\}\}/g, (match, field) => {
      return String(dataFeed[field] || match);
    });
  };

  const processImageUrl = (imageUrl: string, dataBinding?: string, dataFeed?: DataFeed) => {
    if (dataBinding && dataFeed) {
      return String(dataFeed[dataBinding] || imageUrl);
    }
    
    if (!dataFeed) return imageUrl;
    
    return imageUrl.replace(/\{\{(\w+)\}\}/g, (match, field) => {
      return String(dataFeed[field] || match);
    });
  };

  const renderElement = (element: BannerElement, dataFeed: DataFeed) => {
    const elementStyle: React.CSSProperties = {
      position: 'absolute',
      left: element.x * previewScale,
      top: element.y * previewScale,
      width: element.width * previewScale,
      height: element.height * previewScale,
      zIndex: element.zIndex,
      fontSize: (element.styles.fontSize || 16) * previewScale,
      fontWeight: element.styles.fontWeight,
      fontFamily: element.styles.fontFamily,
      textAlign: element.styles.textAlign,
      color: element.styles.color,
      backgroundColor: element.styles.backgroundColor,
      borderRadius: (element.styles.borderRadius || 0) * previewScale,
      padding: (element.styles.padding || 0) * previewScale,
      margin: (element.styles.margin || 0) * previewScale,
      boxShadow: element.styles.boxShadow,
      border: element.styles.border,
      opacity: element.styles.opacity,
      transform: element.styles.transform
    };

    switch (element.type) {
      case 'text':
        return (
          <div
            key={element.id}
            style={{
              ...elementStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {processContent(element.content || 'Text', element.dataBinding, dataFeed)}
          </div>
        );

      case 'image':
        const imageUrl = processImageUrl(element.imageUrl || '', element.dataBinding, dataFeed);
        return (
          <div
            key={element.id}
            style={{
              ...elementStyle,
              overflow: 'hidden'
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Banner Image"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <div className="w-full h-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-500">
                <span style={{ fontSize: '8px' }}>No Image</span>
              </div>
            )}
          </div>
        );

      case 'shape':
        return (
          <div
            key={element.id}
            style={elementStyle}
          />
        );

      case 'button':
        return (
          <button
            key={element.id}
            style={{
              ...elementStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            {processContent(element.content || 'Button', element.dataBinding, dataFeed)}
          </button>
        );

      case 'divider':
        return (
          <div
            key={element.id}
            style={{
              ...elementStyle,
              height: (element.height || 2) * previewScale
            }}
          />
        );

      default:
        return null;
    }
  };

  const renderBannerPreview = (dataFeed: DataFeed, index: number) => {
    const scaledWidth = size.width * previewScale;
    const scaledHeight = size.height * previewScale;

    return (
      <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Banner Preview */}
        <div className="relative bg-gray-50 flex items-center justify-center p-4">
          <div
            className="relative bg-white shadow-sm border border-gray-200"
            style={{
              width: scaledWidth,
              height: scaledHeight,
              minWidth: scaledWidth,
              minHeight: scaledHeight
            }}
          >
            {elements
              .sort((a, b) => a.zIndex - b.zIndex)
              .map(element => renderElement(element, dataFeed))}
          </div>
        </div>

        {/* Data Info */}
        <div className="p-3 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              Variation {index + 1}
            </h4>
            <span className="text-xs text-gray-500">
              {size.width} × {size.height}
            </span>
          </div>
          
          {/* Key data points */}
          <div className="space-y-1">
            {Object.entries(dataFeed).slice(0, 3).map(([key, value]) => (
              <div key={key} className="flex justify-between text-xs">
                <span className="text-gray-600 capitalize">
                  {key.replace(/_/g, ' ')}:
                </span>
                <span className="text-gray-900 font-medium truncate ml-2 max-w-24">
                  {String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const getGridColumns = () => {
    const count = dataFeedArray.length;
    if (count <= 2) return 'grid-cols-1 md:grid-cols-2';
    if (count <= 4) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4';
    if (count <= 6) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl max-h-[95vh] m-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white rounded-t-lg">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">Banner Preview</h2>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <span>{dataFeedArray.length} variations</span>
              <span>•</span>
              <span>{size.name}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Grid View"
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="List View"
              >
                <List size={16} />
              </button>
            </div>

            {/* Scale Controls */}
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setPreviewScale(Math.max(0.1, previewScale - 0.1))}
                className="p-2 text-gray-600 hover:text-gray-900 rounded-md transition-colors"
                title="Zoom Out"
              >
                <ZoomOut size={16} />
              </button>
              <span className="text-sm text-gray-600 min-w-[50px] text-center">
                {Math.round(previewScale * 100)}%
              </span>
              <button
                onClick={() => setPreviewScale(Math.min(1, previewScale + 0.1))}
                className="p-2 text-gray-600 hover:text-gray-900 rounded-md transition-colors"
                title="Zoom In"
              >
                <ZoomIn size={16} />
              </button>
            </div>

            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-gray-50">
          {viewMode === 'grid' ? (
            /* Grid View */
            <div className="p-6">
              <div className={`grid ${getGridColumns()} gap-6`}>
                {dataFeedArray.map((dataFeed, index) => renderBannerPreview(dataFeed, index))}
              </div>
            </div>
          ) : (
            /* List View */
            <div className="p-6 space-y-6">
              {dataFeedArray.map((dataFeed, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="flex">
                    {/* Banner Preview */}
                    <div className="flex-shrink-0 bg-gray-50 p-6 flex items-center justify-center">
                      <div
                        className="relative bg-white shadow-sm border border-gray-200"
                        style={{
                          width: size.width * previewScale,
                          height: size.height * previewScale
                        }}
                      >
                        {elements
                          .sort((a, b) => a.zIndex - b.zIndex)
                          .map(element => renderElement(element, dataFeed))}
                      </div>
                    </div>

                    {/* Data Details */}
                    <div className="flex-1 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-medium text-gray-900">
                          Variation {index + 1}
                        </h4>
                        <span className="text-sm text-gray-500">
                          {size.width} × {size.height}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {Object.entries(dataFeed).map(([key, value]) => (
                          <div key={key} className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                              {key.replace(/_/g, ' ')}
                            </div>
                            <div className="text-sm text-gray-900 font-medium">
                              {String(value)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-white rounded-b-lg">
          <div className="text-sm text-gray-600">
            Showing {dataFeedArray.length} banner variation{dataFeedArray.length !== 1 ? 's' : ''} 
            for {size.name}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Close
            </button>
            <button
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              onClick={() => {
                // This would trigger export of all variations
                console.log('Export all variations');
              }}
            >
              <Download size={16} />
              <span>Export All</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};